import React, { useState, useEffect } from 'react';
import type { LayoutConfig, WidgetConfig } from '../utils/types';
import type { WidgetProps } from './widgets/types';
import { TrendingSearchesWidget } from './widgets/TrendingSearchesWidget';
import { RecentSearchesWidget } from './widgets/RecentSearchesWidget';
import { BrandsWidget } from './widgets/BrandsWidget';
import { BannerWidget } from './widgets/BannerWidget';
import { ArticlesWidget } from './widgets/ArticlesWidget';
import { BestSellerWinesWidget } from './widgets/BestSellerWinesWidget';
import { PromotedWinesWidget } from './widgets/PromotedWinesWidget';
import { CategoriesWidget } from './widgets/CategoriesWidget';
import { FiltersWidget } from './widgets/FiltersWidget';
import { RecentlyViewedWidget } from './widgets/RecentlyViewedWidget';

// ---------------------------------------------------------------------------
// Shared widget renderer
// ---------------------------------------------------------------------------

function renderWidget(widgetConfig: WidgetConfig, props: Omit<WidgetProps, 'config'>) {
  if (!widgetConfig.visible) return null;

  const sharedProps: WidgetProps = { config: widgetConfig, ...props };

  switch (widgetConfig.type) {
    case 'trending-searches':
      return <TrendingSearchesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'recent-searches':
      return <RecentSearchesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'brands':
      return <BrandsWidget key={widgetConfig.id} {...sharedProps} />;
    case 'banner':
      return <BannerWidget key={widgetConfig.id} {...sharedProps} />;
    case 'articles':
      return <ArticlesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'best-seller-wines':
      return <BestSellerWinesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'promoted-wines':
      return <PromotedWinesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'categories':
      return <CategoriesWidget key={widgetConfig.id} {...sharedProps} />;
    case 'filters':
      return <FiltersWidget key={widgetConfig.id} {...sharedProps} />;
    case 'recently-viewed':
      return <RecentlyViewedWidget key={widgetConfig.id} {...sharedProps} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Column grid class by column count
// ---------------------------------------------------------------------------

// Grid classes keyed by actual number of visible columns
const COLUMN_GRID: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LayoutRendererProps extends Omit<WidgetProps, 'config'> {
  layoutConfig: LayoutConfig;
  /** Which zone to render: 'header' renders only headerWidgets, 'columns' renders the column grid */
  zone: 'header' | 'columns';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LayoutRenderer: React.FC<LayoutRendererProps> = ({
  layoutConfig,
  zone,
  ...widgetProps
}) => {
  // Drive the column layout from a JS breakpoint via conditional RENDERING
  // (not CSS show/hide classes). In the embedded widget, host-page CSS can
  // override our `.botilia-show-sm-*` display rules, which left the desktop
  // (narrow inline) filter column showing on mobile instead of stacking.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  if (zone === 'header') {
    const visibleHeader = layoutConfig.headerWidgets.filter((w) => w.visible);
    if (visibleHeader.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2 animate-fade-in">
        {visibleHeader.map((w) => (
          // min-w-0 lets the item shrink below its content's intrinsic width —
          // otherwise widgets with non-wrapping rows (e.g. trending chips)
          // stretch past the viewport and their overflow-x scroll never engages
          <div key={w.id} className="min-w-0 max-w-full">
            {renderWidget(w, widgetProps)}
          </div>
        ))}
      </div>
    );
  }

  // zone === 'columns'
  // Only render columns that have at least one visible widget
  const colCount = layoutConfig.columnCount;
  const allColumns = layoutConfig.columns.slice(0, colCount);
  const visibleColumns = allColumns.filter((col) => col.widgets.some((w) => w.visible));

  if (visibleColumns.length === 0) return null;

  const hasCustomWidths = colCount > 1 && visibleColumns.some((c) => c.widthPercent != null);

  if (hasCustomWidths) {
    const gapPx = 24; // gap-6 = 24px
    // Mobile: stack columns full-width. Desktop: side-by-side with custom widths.
    if (isMobile) {
      return (
        <div className="flex flex-col gap-6">
          {visibleColumns.map((col) => (
            <div key={col.id} className="min-w-0">
              {col.widgets.map((wid) => renderWidget(wid, widgetProps))}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="flex gap-6">
        {visibleColumns.map((col) => {
          const w = col.widthPercent ?? Math.floor(100 / colCount);
          return (
            <div
              key={col.id}
              className="min-w-0"
              style={{ width: `calc(${w}% - ${((colCount - 1) * gapPx) / colCount}px)` }}
            >
              {col.widgets.map((wid) => renderWidget(wid, widgetProps))}
            </div>
          );
        })}
      </div>
    );
  }

  const gridClass = COLUMN_GRID[visibleColumns.length] ?? COLUMN_GRID[2];

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {visibleColumns.map((col) => (
        <div key={col.id} className="min-w-0">
          {col.widgets.map((w) => renderWidget(w, widgetProps))}
        </div>
      ))}
    </div>
  );
};
